import axios from 'axios';
import { getApiBase } from '../apiBase';

const API = axios.create({ baseURL: getApiBase(), withCredentials: true, timeout: 120000 });

/**
 * Static Oracle Fusion SQL review — tables, flags, markdown skeleton.
 * @param {string} sql
 * @param {{ advisorText?: string, source?: string }} [opts]
 */
export async function reviewOracleSql(sql, opts = {}) {
  const res = await API.post(
    '/api/copilot/review-sql',
    {
      sql,
      advisorText: opts.advisorText,
      source: opts.source,
    },
    { validateStatus: () => true }
  );
  if (res.status !== 200) {
    const err = new Error(res.data?.error || `Review failed (HTTP ${res.status})`);
    err.status = res.status;
    err.response = res;
    throw err;
  }
  return res.data;
}

/** Default saved-query name using mm-dd-yyyy (user preference). */
export function formatDefaultQueryName(fallbackTitle) {
  const trimmed = String(fallbackTitle || '').trim();
  if (trimmed) return trimmed.slice(0, 80);
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `Query ${mm}-${dd}-${yyyy}`;
}

/** Personal query library (MongoDB SavedQuery). */
export async function listSavedQueries() {
  const res = await API.get('/api/copilot/saved-queries', { validateStatus: () => true });
  if (res.status !== 200) {
    const err = new Error(res.data?.error || `Could not load saved queries (HTTP ${res.status})`);
    err.status = res.status;
    err.response = res;
    throw err;
  }
  return Array.isArray(res.data?.queries) ? res.data.queries : [];
}

/**
 * Create a new saved query. Does not update curated oracleSqlQueryLibrary recipes.
 * @param {{ name: string, sql: string, mode?: 'oracle'|'otbi', notes?: string }} payload
 */
export async function saveSqlQuery(payload) {
  const name = String(payload?.name || '').trim();
  const sql = String(payload?.sql || '').trim();
  if (!name || !sql) {
    throw new Error('name and sql are required');
  }
  const res = await API.post(
    '/api/copilot/saved-queries',
    {
      name,
      sql,
      mode: payload.mode === 'otbi' ? 'otbi' : 'oracle',
      notes: String(payload?.notes || '').trim(),
    },
    { validateStatus: () => true }
  );
  if (res.status !== 201) {
    const err = new Error(
      res.data?.error ||
        (res.status === 503
          ? 'Saved queries require MongoDB — set MONGODB_URI in .env and restart the server.'
          : `Save failed (HTTP ${res.status})`)
    );
    err.status = res.status;
    err.response = res;
    throw err;
  }
  return res.data?.query || null;
}

/**
 * Update an existing saved query owned by the current user.
 * @param {string} id
 * @param {{ name?: string, sql: string, mode?: 'oracle'|'otbi', notes?: string }} payload
 */
export async function updateSqlQuery(id, payload) {
  const sql = String(payload?.sql || '').trim();
  if (!id || !sql) {
    throw new Error('id and sql are required');
  }
  const body = { sql };
  const name = String(payload?.name || '').trim();
  if (name) body.name = name;
  if (payload.mode === 'otbi' || payload.mode === 'oracle') body.mode = payload.mode;
  if (payload.notes !== undefined) body.notes = String(payload.notes || '').trim();

  const res = await API.put(`/api/copilot/saved-queries/${id}`, body, { validateStatus: () => true });
  if (res.status !== 200) {
    const err = new Error(
      res.data?.error ||
        (res.status === 503
          ? 'Saved queries require MongoDB — set MONGODB_URI in .env and restart the server.'
          : res.status === 404
            ? 'Saved query not found — it may have been deleted.'
            : `Update failed (HTTP ${res.status})`)
    );
    err.status = res.status;
    err.response = res;
    throw err;
  }
  return res.data?.query || null;
}

/** Default and max row limits — must stay aligned with server.js clampRowLimit caps. */
export const SQL_STUDIO_DEFAULT_ROW_LIMIT = 100;
export const SQL_STUDIO_OTBI_ROW_LIMIT_MAX = 1000;
export const SQL_STUDIO_ORACLE_ROW_LIMIT_MAX = 15000;
const ROW_LIMIT_STORAGE_KEY = 'aiTool.sqlStudio.rowLimit.v1';

export function clampSqlStudioRowLimit(raw, modeId = 'oracle') {
  const max =
    modeId === 'otbi' ? SQL_STUDIO_OTBI_ROW_LIMIT_MAX : SQL_STUDIO_ORACLE_ROW_LIMIT_MAX;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return SQL_STUDIO_DEFAULT_ROW_LIMIT;
  return Math.min(Math.max(n, 1), max);
}

export function loadSqlStudioRowLimit() {
  try {
    const n = parseInt(localStorage.getItem(ROW_LIMIT_STORAGE_KEY), 10);
    if (Number.isFinite(n) && n >= 1) return n;
  } catch {
    /* localStorage unavailable */
  }
  return SQL_STUDIO_DEFAULT_ROW_LIMIT;
}

export function saveSqlStudioRowLimit(value) {
  try {
    localStorage.setItem(ROW_LIMIT_STORAGE_KEY, String(value));
  } catch {
    /* localStorage unavailable */
  }
}
